import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useAuth } from '@/components/auth/AuthProvider'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { authApi } from '@/lib/api/auth.api'
import { createLogger } from '@/lib/logger'
import { realtimeStore } from '@/lib/realtime/store'
import { isPopupWindow } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { useLocation } from 'react-router-dom'
import io, { Socket } from 'socket.io-client'
import { toast } from 'sonner'

import { SessionChangedError } from '@scrambl/shared/session-scope'

const logger = createLogger('lib/socket/SocketContext')
const SOCKET_RECONNECT_REQUEST_EVENT = 'scrambl:socket-reconnect-request'

export function requestSocketReconnect(reason = 'manual') {
  if (typeof window === 'undefined') {
    return
  }

  window.dispatchEvent(
    new CustomEvent(SOCKET_RECONNECT_REQUEST_EVENT, {
      detail: { reason },
    }),
  )
}

// Helper function to get the correct API URL from environment
function getApiUrl(): string {
  // Use build-time environment variable, then localhost
  return import.meta.env.VITE_API_URL || 'http://localhost:4005'
}

// Token refresh constants - more aggressive to prevent silent expiration
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000 // 25 minutes (well before 1-hour expiry for safety)
const TOKEN_REFRESH_BUFFER = 15 * 60 * 1000 // 15 minutes before expiry, proactively refresh

// Adaptive health check constants - more frequent for faster detection
const HEALTH_CHECK_INTERVAL_MIN = 2 * 60 * 1000 // 2 minutes - initial interval (was 5)
const HEALTH_CHECK_INTERVAL_MAX = 5 * 60 * 1000 // 5 minutes - max interval (was 15)
const HEALTH_CHECK_STABLE_THRESHOLD = 3 // Number of successful checks before increasing interval
const CONNECTION_LIMIT_BACKOFF_MS = 35 * 1000 // Slightly above backend's 30s block window
const WAKE_RECONNECT_IDLE_MS = 5 * 60 * 1000 // Treat a long event gap as app sleep/hibernation
const WAKE_RECONNECT_THROTTLE_MS = 60 * 1000
const CONNECTION_ISSUE_TOAST_DELAY_MS = 15 * 1000
const CONNECTION_ISSUE_TOAST_COOLDOWN_MS = 5 * 60 * 1000
const CONNECTION_ISSUE_TOAST_ID = 'socket-connection-issue'

function isAuthError(error: unknown) {
  const message =
    typeof error === 'string' ? error : (error as Error | undefined)?.message
  if (!message) {
    return false
  }

  const normalized = message.toLowerCase()
  return (
    normalized.includes('401') ||
    normalized.includes('unauthorized') ||
    normalized.includes('authentication failed') ||
    normalized.includes('token expired') ||
    normalized.includes('invalid token')
  )
}

function isBrowserOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

const PING_PROBE_TIMEOUT_MS = 5 * 1000
const REFRESH_ACK_TIMEOUT_MS = 10 * 1000

// Probe a connected socket with a ping and resolve true if the server acks in time
function pingSocket(
  socketInstance: Socket,
  timeoutMs: number = PING_PROBE_TIMEOUT_MS,
): Promise<boolean> {
  return new Promise(resolve => {
    let settled = false
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true
        resolve(false)
      }
    }, timeoutMs)

    socketInstance.emit('ping', () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(true)
    })
  })
}

// Rotate the auth token on a live connection without disconnecting.
// Resolves false if the server rejects the token or doesn't ack in time,
// in which case the caller should fall back to a full reconnect.
function refreshTokenInPlace(
  socketInstance: Socket,
  token: string,
): Promise<boolean> {
  return new Promise(resolve => {
    let settled = false
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true
        logger.warn(
          '[SocketProvider] In-place token refresh timed out waiting for server ack',
        )
        resolve(false)
      }
    }, REFRESH_ACK_TIMEOUT_MS)

    socketInstance.emit(
      'refresh_token',
      { token },
      (response?: { success?: boolean; error?: string }) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        if (!response?.success) {
          logger.warn(
            '[SocketProvider] Server rejected in-place token refresh',
            {
              error: response?.error,
            },
          )
        }
        resolve(Boolean(response?.success))
      },
    )
  })
}

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
  isReady: boolean
  socketId: string | null // Exposed socket ID - changes when socket reconnects with new ID
  requestReconnect: (reason?: string) => void
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  isReady: false,
  socketId: null,
  requestReconnect: requestSocketReconnect,
})

interface SocketProviderProps {
  children: ReactNode
}

/**
 * Provider for socket.io connections
 * Handles authentication and reconnection
 */
export function SocketProvider({ children }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [socketId, setSocketId] = useState<string | null>(null)
  const { user, refreshToken } = useAuth()
  const { pathname } = useLocation()
  const currentOrganization = useOrganizationStore(s => s.currentOrganization)
  const externalRelationship = useOrganizationStore(
    s => s.externalOrgRelationship,
  )
  const workspaceSession = useWorkspaceSession()
  const accessKey = JSON.stringify(workspaceSession.key)
  const organizationId = currentOrganization?.id ?? null
  const isClientPortalRoute = pathname.startsWith('/client-portal')
  const shouldDeferSocketInit = !isClientPortalRoute && isPopupWindow()
  const scopedOrganizationId = isClientPortalRoute ? null : organizationId
  const externalRelationshipId =
    !isClientPortalRoute && currentOrganization?.isExternalAccess
      ? externalRelationship?.id
      : null
  const canConnectSocket =
    !!user?.id &&
    user.id === workspaceSession.ownerUserId &&
    (isClientPortalRoute || workspaceSession.enabled)
  const socketAccessKey = JSON.stringify([accessKey, isClientPortalRoute])
  const activeAccessKeyRef = useRef(socketAccessKey)
  activeAccessKeyRef.current = socketAccessKey
  const socketScopeCleanupRef = useRef<(() => void) | null>(null)
  const lastAccessKeyRef = useRef<string | null>(null)
  const reconnectAttempts = useRef(0)
  const socketRef = useRef<Socket | null>(null)
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const healthCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const authReconnectInFlightRef = useRef(false)
  const authRefreshOperationRef = useRef<symbol | null>(null)
  const tokenTimestampRef = useRef<number>(0) // Track when token was last refreshed
  const refreshSocketAuthRef = useRef<
    (forceRefresh?: boolean) => Promise<void>
  >(() => Promise.resolve())
  const reconnectBlockedUntilRef = useRef(0)
  const reconnectBlockTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const connectionIssueToastTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastConnectionIssueToastAtRef = useRef(0)
  const socketInitVersionRef = useRef(0)
  const lastLifecycleEventAtRef = useRef(Date.now())
  const lastWakeReconnectAtRef = useRef(0)

  // Adaptive health check state
  const consecutiveSuccessfulChecks = useRef(0)
  const currentHealthCheckInterval = useRef(HEALTH_CHECK_INTERVAL_MIN)

  // Generate a unique tab ID for this session (persists across reconnections)
  const tabIdRef = useRef(
    `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
  )
  const lastUserIdRef = useRef<string | null>(null) // Track last user ID to prevent unnecessary reconnections
  const lastOrgIdRef = useRef<string | null>(null) // Track last org ID to prevent unnecessary reconnections
  const lastSocketIdRef = useRef<string | null>(null) // Track socket ID to detect reconnections
  const getSocketTokenWithRefresh = async (forceRefresh: boolean = false) => {
    const assertCurrent = () => {
      workspaceSession.assertUnchanged()
      if (!canConnectSocket || activeAccessKeyRef.current !== socketAccessKey)
        throw new SessionChangedError()
    }
    assertCurrent()
    const complete = (token: string) => {
      assertCurrent()
      tokenTimestampRef.current = Date.now()
      return {
        auth: {
          userId: user!.id,
          organizationId: scopedOrganizationId,
          externalRelationshipId,
          token,
          tabId: tabIdRef.current,
        },
        signal: workspaceSession.signal,
        assertCurrent,
      }
    }
    if (forceRefresh) {
      const refreshed = await refreshToken()
      assertCurrent()
      if (!refreshed) throw new Error('Socket auth refresh failed')
    }
    try {
      const { token } = await authApi.getSocketToken()
      return complete(token)
    } catch (error) {
      assertCurrent()
      if (!forceRefresh && isAuthError(error)) {
        const refreshed = await refreshToken()
        assertCurrent()
        if (!refreshed) throw error
        const { token } = await authApi.getSocketToken()
        return complete(token)
      }
      throw error
    }
  }

  // Check if token is nearing expiry and needs refresh
  const isTokenNearExpiry = () => {
    if (tokenTimestampRef.current === 0) return false
    const tokenAge = Date.now() - tokenTimestampRef.current
    const TOKEN_EXPIRY = 60 * 60 * 1000 // 1 hour
    return tokenAge > TOKEN_EXPIRY - TOKEN_REFRESH_BUFFER
  }

  // Schedule the next adaptive health check
  const scheduleNextHealthCheck = () => {
    if (healthCheckTimeoutRef.current) {
      clearTimeout(healthCheckTimeoutRef.current)
    }

    healthCheckTimeoutRef.current = setTimeout(() => {
      performHealthCheckRef.current()
    }, currentHealthCheckInterval.current)
  }

  // Ref to hold performHealthCheck to avoid circular dependencies
  const performHealthCheckRef = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  )

  // Health check function - verifies connection is still valid
  // Uses adaptive intervals: starts at 2 min, increases to 5 min if stable
  const performHealthCheck = async () => {
    if (!socketRef.current?.connected || !user?.id) {
      logger.debug(
        '[SocketProvider] Skipping health check - socket not connected or no user',
      )
      return
    }

    const checkedSocket = socketRef.current
    const checkedAccessKey = activeAccessKeyRef.current
    const stillCurrent = () =>
      checkedSocket === socketRef.current &&
      checkedAccessKey === activeAccessKeyRef.current
    const tokenAge = tokenTimestampRef.current
      ? Date.now() - tokenTimestampRef.current
      : null
    const tokenNearExpiry = isTokenNearExpiry()

    logger.debug('[SocketProvider] 🏥 Performing socket health check...', {
      socketId: socketRef.current.id,
      socketConnected: socketRef.current.connected,
      tokenAgeMs: tokenAge,
      tokenNearExpiry,
      consecutiveSuccessfulChecks: consecutiveSuccessfulChecks.current,
      currentInterval: currentHealthCheckInterval.current,
      timestamp: new Date().toISOString(),
    })

    // Check if token is nearing expiry
    if (tokenNearExpiry) {
      logger.info(
        '[SocketProvider] ⏰ Token nearing expiry, proactively refreshing...',
        {
          tokenAgeMs: tokenAge,
        },
      )
      await refreshSocketAuthRef.current()
      if (!stillCurrent()) return
      // Reset adaptive interval on refresh
      consecutiveSuccessfulChecks.current = 0
      currentHealthCheckInterval.current = HEALTH_CHECK_INTERVAL_MIN
      scheduleNextHealthCheck()
      return
    }

    // Send a ping to verify the connection is still alive
    // The server should respond - if it doesn't within timeout, connection is stale
    const pingStart = Date.now()
    const pingTimeout = setTimeout(() => {
      if (!stillCurrent()) return
      logger.warn(
        '[SocketProvider] ⏱️ Health check timeout - connection may be stale',
        {
          socketId: socketRef.current?.id,
          socketConnected: socketRef.current?.connected,
          duration: Date.now() - pingStart,
          timestamp: new Date().toISOString(),
        },
      )
      // Reset adaptive interval on failure
      consecutiveSuccessfulChecks.current = 0
      currentHealthCheckInterval.current = HEALTH_CHECK_INTERVAL_MIN
      // Force reconnection with fresh token
      void refreshSocketAuthRef.current(true)
    }, 10000) // 10 second timeout for pong

    checkedSocket.emit('ping', () => {
      clearTimeout(pingTimeout)
      if (!stillCurrent()) return
      // Connection is healthy - update adaptive interval
      consecutiveSuccessfulChecks.current += 1

      // Log every 5th successful check to avoid spam
      if (consecutiveSuccessfulChecks.current % 5 === 0) {
        logger.debug('[SocketProvider] ✅ Socket health check passed', {
          consecutiveSuccessfulChecks: consecutiveSuccessfulChecks.current,
          pingDuration: Date.now() - pingStart,
          nextInterval: currentHealthCheckInterval.current,
        })
      }

      // Increase interval if connection has been stable
      if (
        consecutiveSuccessfulChecks.current >= HEALTH_CHECK_STABLE_THRESHOLD
      ) {
        currentHealthCheckInterval.current = Math.min(
          currentHealthCheckInterval.current * 1.5,
          HEALTH_CHECK_INTERVAL_MAX,
        )
      }

      // Schedule next health check
      scheduleNextHealthCheck()
    })
  }

  // Keep the ref in sync
  performHealthCheckRef.current = performHealthCheck

  const clearConnectionTimeout = () => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current)
      connectionTimeoutRef.current = null
    }
  }

  const clearConnectionIssueToast = () => {
    if (connectionIssueToastTimeoutRef.current) {
      clearTimeout(connectionIssueToastTimeoutRef.current)
      connectionIssueToastTimeoutRef.current = null
    }
    toast.dismiss(CONNECTION_ISSUE_TOAST_ID)
  }

  const scheduleConnectionIssueToast = (socketInstance: Socket) => {
    if (connectionIssueToastTimeoutRef.current) {
      return
    }

    connectionIssueToastTimeoutRef.current = setTimeout(() => {
      connectionIssueToastTimeoutRef.current = null

      const now = Date.now()
      const isStillDisconnected =
        socketRef.current === socketInstance &&
        !socketInstance.connected &&
        Boolean(user?.id)
      const isOutsideCooldown =
        now - lastConnectionIssueToastAtRef.current >=
        CONNECTION_ISSUE_TOAST_COOLDOWN_MS

      if (!isStillDisconnected || !isOutsideCooldown) {
        return
      }

      lastConnectionIssueToastAtRef.current = now
      toast.warning('Connection issue detected', {
        id: CONNECTION_ISSUE_TOAST_ID,
        description:
          'Still attempting to reconnect. Your changes may not be saving.',
        duration: 8000,
      })
    }, CONNECTION_ISSUE_TOAST_DELAY_MS)
  }

  const isReconnectBlocked = () => reconnectBlockedUntilRef.current > Date.now()

  const clearReconnectBlock = () => {
    reconnectBlockedUntilRef.current = 0
    if (reconnectBlockTimeoutRef.current) {
      clearTimeout(reconnectBlockTimeoutRef.current)
      reconnectBlockTimeoutRef.current = null
    }
  }

  const scheduleReconnectAfterCooldown = (socketInstance: Socket) => {
    if (reconnectBlockTimeoutRef.current) {
      clearTimeout(reconnectBlockTimeoutRef.current)
    }

    const waitMs =
      Math.max(reconnectBlockedUntilRef.current - Date.now(), 0) + 250
    reconnectBlockTimeoutRef.current = setTimeout(() => {
      if (
        socketRef.current !== socketInstance ||
        !user?.id ||
        !isBrowserOnline()
      ) {
        return
      }

      logger.info(
        '[SocketProvider] Connection-limit cooldown elapsed, retrying socket connection',
      )
      socketInstance.io.opts.reconnection = true
      setupConnectionTimeout()
      socketInstance.connect()
    }, waitMs)
  }

  const setupConnectionTimeout = () => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current)
      connectionTimeoutRef.current = null
    }

    // Set a timeout to retry if it doesn't connect within 15 seconds
    connectionTimeoutRef.current = setTimeout(() => {
      if (socketRef.current && !socketRef.current.connected) {
        if (!isBrowserOnline()) {
          logger.debug(
            '[SocketProvider] Skipping connection timeout retry while offline',
          )
          return
        }
        if (isReconnectBlocked()) {
          logger.info(
            '[SocketProvider] Skipping connection timeout retry during reconnect cooldown',
          )
          scheduleReconnectAfterCooldown(socketRef.current)
          return
        }
        logger.warn('[SocketProvider] Connection timeout, retrying...')
        socketRef.current.connect()
      }
    }, 15000)
  }

  const refreshSocketAuth = async (forceRefresh: boolean = false) => {
    if (!socketRef.current || !user?.id) {
      logger.debug('[SocketProvider] Skipping auth refresh - no socket or user')
      return
    }

    if (authReconnectInFlightRef.current) {
      logger.debug('[SocketProvider] Auth refresh already in flight, skipping')
      return
    }

    if (!isBrowserOnline()) {
      logger.info(
        '[SocketProvider] Browser offline, deferring socket auth refresh',
      )
      return
    }

    if (isReconnectBlocked()) {
      logger.info(
        '[SocketProvider] Reconnect cooldown active, deferring socket auth refresh',
      )
      return
    }

    const refreshStart = Date.now()
    logger.info('[SocketProvider] 🔄 Refreshing socket auth...', {
      forceRefresh,
      userId: user.id,
      socketId: socketRef.current.id,
      socketConnected: socketRef.current.connected,
      timestamp: new Date().toISOString(),
    })

    const refreshingSocket = socketRef.current
    const refreshOperation = Symbol()
    authRefreshOperationRef.current = refreshOperation
    authReconnectInFlightRef.current = true
    try {
      // Get new token BEFORE disconnecting - this way if token refresh fails,
      // the socket stays connected with the old (possibly still valid) token
      const credentials = await getSocketTokenWithRefresh(forceRefresh)
      credentials.assertCurrent()
      if (socketRef.current !== refreshingSocket) return
      const socketToken = credentials.auth.token
      refreshingSocket.auth = credentials.auth
      reconnectAttempts.current = 0

      // For proactive refreshes (token rotation), update the live session
      // without tearing down a healthy connection. Forced refreshes mean
      // something is wrong with the connection, so go straight to reconnect.
      if (!forceRefresh && socketRef.current.connected) {
        const refreshedInPlace = await refreshTokenInPlace(
          refreshingSocket,
          socketToken,
        )
        credentials.assertCurrent()
        if (socketRef.current !== refreshingSocket) return
        if (refreshedInPlace) {
          logger.info('[SocketProvider] ✅ Socket token refreshed in place', {
            duration: Date.now() - refreshStart,
            socketId: socketRef.current.id,
          })
          return
        }
        if (!socketRef.current) {
          return
        }
        logger.warn(
          '[SocketProvider] In-place token refresh failed, falling back to reconnect',
        )
      }

      setupConnectionTimeout()
      socketRef.current.disconnect()
      socketRef.current.connect()

      logger.info('[SocketProvider] ✅ Socket auth refresh initiated', {
        duration: Date.now() - refreshStart,
        newSocketId: socketRef.current.id,
      })
    } catch (error) {
      if (
        error instanceof SessionChangedError ||
        refreshingSocket !== socketRef.current ||
        activeAccessKeyRef.current !== socketAccessKey
      )
        return
      logger.error('[SocketProvider] ❌ Socket auth refresh failed', {
        error: error instanceof Error ? error.message : error,
        duration: Date.now() - refreshStart,
        timestamp: new Date().toISOString(),
      })

      // If we're still connected, don't show error - the existing connection might still work
      if (socketRef.current?.connected) {
        logger.warn(
          '[SocketProvider] Token refresh failed but socket still connected, will retry later',
        )
        // Schedule a retry after a short delay
        setTimeout(() => {
          if (
            socketRef.current === refreshingSocket &&
            refreshingSocket.connected &&
            activeAccessKeyRef.current === socketAccessKey
          ) {
            logger.info('[SocketProvider] Retrying token refresh...')
            void refreshSocketAuthRef.current(true)
          }
        }, 30000) // Retry in 30 seconds
      } else {
        // Socket is disconnected and we couldn't get a new token
        // Try to reconnect with existing auth - server might still accept it
        logger.warn(
          '[SocketProvider] Token refresh failed and socket disconnected, attempting reconnect with existing auth',
        )
        try {
          if (!isReconnectBlocked()) {
            socketRef.current.connect()
          }
        } catch {
          // If reconnect also fails, show the error to user
          toast.error('Connection lost', {
            description:
              'Unable to reconnect. Your recent changes may not have been saved. Refresh the page.',
            duration: 10000,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload(),
            },
          })
        }
      }
    } finally {
      if (authRefreshOperationRef.current === refreshOperation) {
        authRefreshOperationRef.current = null
        authReconnectInFlightRef.current = false
      }
    }
  }

  // Keep the ref in sync with the callback
  refreshSocketAuthRef.current = refreshSocketAuth

  const registerSocketHandlers = (socketInstance: Socket) => {
    socketInstance.on('connect', () => {
      if (socketRef.current !== socketInstance) return
      // Clear connection timeout
      clearConnectionTimeout()
      clearConnectionIssueToast()
      clearReconnectBlock()
      socketInstance.io.opts.reconnection = true

      // Detect if this is a reconnection (socket ID changed)
      const currentSocketId = socketInstance.id
      const isReconnection =
        lastSocketIdRef.current !== null &&
        lastSocketIdRef.current !== currentSocketId

      logger.info('[SocketProvider] ✅ Socket connected', {
        socketId: currentSocketId,
        previousSocketId: lastSocketIdRef.current,
        isReconnection,
        userId: user?.id,
        timestamp: new Date().toISOString(),
      })

      if (isReconnection) {
        // Clear room memberships because the server loses them on reconnection
        // This forces rooms to be re-joined with the new socket
        logger.info(
          '[SocketProvider] Socket reconnected with new ID, clearing room memberships',
        )
        realtimeStore.clearRoomMemberships()
      }

      // Update the tracked socket ID
      lastSocketIdRef.current = currentSocketId ?? null

      setIsConnected(true)
      setIsReady(true)
      setSocketId(currentSocketId ?? null) // Update state to trigger re-renders in consumers
      reconnectAttempts.current = 0
      tokenTimestampRef.current = Date.now() // Mark token as fresh on connect

      // Set up token refresh - refresh every 25 minutes (well before 1-hour expiry)
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current)
      }

      logger.info('[SocketProvider] 📅 Setting up token refresh interval', {
        intervalMs: TOKEN_REFRESH_INTERVAL,
        intervalMinutes: TOKEN_REFRESH_INTERVAL / 60000,
      })

      tokenRefreshIntervalRef.current = setInterval(async () => {
        logger.info('[SocketProvider] ⏰ Token refresh interval triggered', {
          tokenAgeMs: tokenTimestampRef.current
            ? Date.now() - tokenTimestampRef.current
            : null,
          socketConnected: socketRef.current?.connected,
          timestamp: new Date().toISOString(),
        })
        await refreshSocketAuthRef.current()
      }, TOKEN_REFRESH_INTERVAL)

      // Set up adaptive health check - starts at 5 min, increases if stable
      consecutiveSuccessfulChecks.current = 0
      currentHealthCheckInterval.current = HEALTH_CHECK_INTERVAL_MIN
      scheduleNextHealthCheck()
    })

    socketInstance.on('disconnect', reason => {
      if (socketRef.current !== socketInstance) return
      logger.warn('[SocketProvider] 🔌 Socket disconnected', {
        reason,
        socketId: socketInstance.id,
        userId: user?.id,
        timestamp: new Date().toISOString(),
      })
      setIsConnected(false)
      setIsReady(false)

      // Clear health check on disconnect
      if (healthCheckTimeoutRef.current) {
        clearTimeout(healthCheckTimeoutRef.current)
        healthCheckTimeoutRef.current = null
      }

      // Handle different disconnect reasons
      // - 'io server disconnect': Server explicitly disconnected us
      // - 'io client disconnect': Client called disconnect() (e.g. during auth refresh)
      // - 'ping timeout': Server didn't respond to ping in time
      // - 'transport close': Network issue or server went down
      // - 'transport error': Transport error occurred
      if (user?.id) {
        if (isReconnectBlocked()) {
          logger.info(
            '[SocketProvider] Reconnect cooldown active, skipping disconnect reconnect handling',
          )
          return
        }
        if (reason === 'io server disconnect') {
          scheduleConnectionIssueToast(socketInstance)
          // Server disconnected us - reconnect immediately
          logger.info(
            '[SocketProvider] Server disconnected us, attempting to reconnect...',
          )
          socketInstance.connect()
        } else if (
          reason === 'ping timeout' ||
          reason === 'transport close' ||
          reason === 'transport error'
        ) {
          scheduleConnectionIssueToast(socketInstance)
          // Network issue - reconnect with fresh token
          logger.info(
            '[SocketProvider] Network issue detected, refreshing auth and reconnecting...',
            { reason },
          )
          void refreshSocketAuthRef.current(true)
        } else if (reason === 'io client disconnect') {
          // Client disconnect is usually intentional (during auth refresh), but we should
          // verify the socket reconnects. If it doesn't reconnect within a few seconds,
          // something went wrong and we should try to reconnect.
          setTimeout(() => {
            if (
              socketRef.current === socketInstance &&
              !socketInstance.connected &&
              user?.id &&
              !authReconnectInFlightRef.current
            ) {
              logger.warn(
                '[SocketProvider] Socket still disconnected after client disconnect, attempting reconnect...',
              )
              socketInstance.connect()
            }
          }, 5000) // Wait 5 seconds for normal reconnect flow to complete
        }
      }
    })

    socketInstance.on('connect_error', error => {
      if (socketRef.current !== socketInstance) return
      const authError = isAuthError(error)
      const normalizedMessage = (error?.message || '').toLowerCase()
      const isTransportError =
        normalizedMessage.includes('xhr poll error') ||
        normalizedMessage.includes('websocket error') ||
        normalizedMessage.includes('network error') ||
        normalizedMessage.includes('load failed') ||
        normalizedMessage.includes('failed to fetch')
      const isConnectionLimitError = normalizedMessage.includes(
        'too many connections from this ip address',
      )

      logger.error('[SocketProvider] ❌ Connection error', {
        message: error.message,
        reconnectAttempts: reconnectAttempts.current + 1,
        isAuthError: authError,
        isTransportError,
        isConnectionLimitError,
        timestamp: new Date().toISOString(),
      })
      reconnectAttempts.current += 1

      if (isConnectionLimitError) {
        const wasBlocked = isReconnectBlocked()
        reconnectBlockedUntilRef.current = Math.max(
          reconnectBlockedUntilRef.current,
          Date.now() + CONNECTION_LIMIT_BACKOFF_MS,
        )

        logger.warn(
          '[SocketProvider] Server connection limit reached, backing off reconnect attempts',
          {
            reconnectBlockedUntil: new Date(
              reconnectBlockedUntilRef.current,
            ).toISOString(),
            reconnectAttempts: reconnectAttempts.current,
          },
        )

        if (!wasBlocked) {
          toast.warning('Realtime temporarily rate-limited', {
            description:
              'Too many reconnect attempts were detected. Retrying shortly.',
            duration: 5000,
          })
        }

        clearConnectionTimeout()
        socketInstance.io.opts.reconnection = false
        socketInstance.disconnect()
        scheduleReconnectAfterCooldown(socketInstance)
        return
      }

      if (authError) {
        void refreshSocketAuthRef.current(true)
        return
      }

      if (!isBrowserOnline()) {
        logger.info(
          '[SocketProvider] Browser offline during socket connect_error, waiting for online event',
        )
        return
      }

      if (isTransportError && reconnectAttempts.current % 5 === 0) {
        logger.warn(
          '[SocketProvider] Repeated transport errors while online, forcing reconnect',
          {
            attempts: reconnectAttempts.current,
            message: error.message,
          },
        )
        socketInstance.io.opts.transports = ['websocket', 'polling']
        socketInstance.io.opts.rememberUpgrade = true
        setupConnectionTimeout()
      }
    })

    socketInstance.on('error', error => {
      if (socketRef.current !== socketInstance) return
      logger.error('[SocketProvider] ❌ Socket error', {
        error,
        socketId: socketInstance.id,
        connected: socketInstance.connected,
        timestamp: new Date().toISOString(),
      })
      // Check if this is an auth error during operation
      if (isAuthError(error)) {
        logger.info(
          '[SocketProvider] Auth error detected, refreshing socket auth...',
        )
        toast.warning('Session issue detected', {
          description: 'Reconnecting to save your changes…',
          duration: 4000,
        })
        void refreshSocketAuthRef.current(true)
      }
    })

    // Listen for server-side auth errors (token expired, unauthorized, etc.)
    // The server may emit these when validating operations with expired tokens
    socketInstance.on('unauthorized', (error: { message?: string }) => {
      if (socketRef.current !== socketInstance) return
      logger.warn(
        '[SocketProvider] 🔐 Received unauthorized event from server',
        {
          message: error?.message,
          socketId: socketInstance.id,
          timestamp: new Date().toISOString(),
        },
      )
      toast.error('Session expired', {
        description: 'Your session has expired. Attempting to refresh…',
        duration: 5000,
      })
      void refreshSocketAuthRef.current(true)
    })

    socketInstance.on('auth_error', (error: { message?: string }) => {
      if (socketRef.current !== socketInstance) return
      logger.warn(
        '[SocketProvider] Received auth_error event from server:',
        error?.message,
      )
      toast.error('Authentication error', {
        description: 'There was a problem with your session. Reconnecting…',
        duration: 5000,
      })
      void refreshSocketAuthRef.current(true)
    })

    // Handle token expiration notification from server
    socketInstance.on('token_expired', () => {
      if (socketRef.current !== socketInstance) return
      logger.warn(
        '[SocketProvider] Server notified token expired, refreshing...',
      )
      toast.warning('Session refreshing', {
        description: 'Refreshing your session…',
        duration: 4000,
      })
      void refreshSocketAuthRef.current(true)
    })
  }

  const createSocketConnection = async (
    credentials: Awaited<ReturnType<typeof getSocketTokenWithRefresh>>,
  ) => {
    credentials.assertCurrent()
    socketScopeCleanupRef.current?.()

    if (socketRef.current) {
      socketRef.current.removeAllListeners()
      socketRef.current.disconnect()
      socketRef.current = null
    }

    const socketInstance = io(getApiUrl(), {
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000, // 20 second timeout
      transports: ['websocket', 'polling'],
      rememberUpgrade: true,
      auth: credentials.auth,
    })

    // Keep a ref to the socket instance for cleanup
    socketRef.current = socketInstance
    const abort = () => {
      socketInstance.removeAllListeners()
      socketInstance.disconnect()
      if (socketRef.current === socketInstance) {
        socketRef.current = null
        setSocket(null)
        setIsConnected(false)
        setIsReady(false)
        setSocketId(null)
      }
    }
    credentials.signal.addEventListener('abort', abort, { once: true })
    socketScopeCleanupRef.current = () =>
      credentials.signal.removeEventListener('abort', abort)

    // Set up connection timeout
    setupConnectionTimeout()

    // Connection event handlers
    registerSocketHandlers(socketInstance)

    // Set socket in state for consumers
    setSocket(socketInstance)

    // Expose socket to window for debugging
    if (typeof window !== 'undefined') {
      ;(window as Window & { __socket?: Socket }).__socket = socketInstance
    }
  }

  const getSocketTokenWithRefreshRef = useRef(getSocketTokenWithRefresh)
  const registerSocketHandlersRef = useRef(registerSocketHandlers)
  const createSocketConnectionRef = useRef(createSocketConnection)

  useEffect(() => {
    getSocketTokenWithRefreshRef.current = getSocketTokenWithRefresh
    registerSocketHandlersRef.current = registerSocketHandlers
    createSocketConnectionRef.current = createSocketConnection
  })

  // Connect to socket when user is authenticated
  useEffect(() => {
    // Only reconnect if user ID and org ID actually changed (not just object recreation)
    if (
      user?.id === lastUserIdRef.current &&
      scopedOrganizationId === lastOrgIdRef.current &&
      socketAccessKey === lastAccessKeyRef.current &&
      socketRef.current
    ) {
      return
    }

    // Update the last user ID and org ID
    lastUserIdRef.current = user?.id || null
    lastOrgIdRef.current = scopedOrganizationId
    lastAccessKeyRef.current = socketAccessKey

    // Clean up previous socket if it exists
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
    }
    clearConnectionTimeout()

    if (!canConnectSocket) {
      setSocket(null)
      setIsConnected(false)
      setIsReady(false)
      setSocketId(null)
      return
    }

    // Store current user ID in localStorage for use in other components
    localStorage.setItem('currentUserId', user.id)
    const initVersion = ++socketInitVersionRef.current
    let cancelled = false
    let initTimer: number | null = null

    // Initialize socket connection asynchronously, waiting for server token
    const initSocket = async () => {
      try {
        const socketToken = await getSocketTokenWithRefreshRef.current()
        if (cancelled || initVersion !== socketInitVersionRef.current) {
          return
        }
        await createSocketConnectionRef.current(socketToken)
      } catch (error) {
        logger.error('[SocketProvider] Error initializing socket:', error)
      }
    }

    if (shouldDeferSocketInit) {
      initTimer = window.setTimeout(() => {
        void initSocket()
      }, 1200)
    } else {
      void initSocket()
    }

    // Cleanup on unmount
    return () => {
      cancelled = true
      authRefreshOperationRef.current = null
      authReconnectInFlightRef.current = false
      socketScopeCleanupRef.current?.()
      socketScopeCleanupRef.current = null
      if (initTimer !== null) {
        window.clearTimeout(initTimer)
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current)
        connectionTimeoutRef.current = null
      }

      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current)
        tokenRefreshIntervalRef.current = null
      }

      if (healthCheckTimeoutRef.current) {
        clearTimeout(healthCheckTimeoutRef.current)
        healthCheckTimeoutRef.current = null
      }

      clearReconnectBlock()
      clearConnectionIssueToast()

      if (socketRef.current) {
        socketRef.current.removeAllListeners()
        socketRef.current.disconnect()
        socketRef.current = null
      }
      setSocket(null)
      setIsConnected(false)
      setIsReady(false)
    }
  }, [
    user?.id,
    scopedOrganizationId,
    isClientPortalRoute,
    shouldDeferSocketInit,
    socketAccessKey,
    canConnectSocket,
  ])

  useEffect(() => {
    if (!user?.id || !scopedOrganizationId || typeof window === 'undefined') {
      return
    }

    let reconnectInFlight = false

    const attemptReconnect = async (options?: {
      force?: boolean
      reason?: string
    }) => {
      const force = options?.force ?? false

      if (reconnectInFlight || isReconnectBlocked()) {
        return
      }

      if (socketRef.current?.connected && !force) {
        return
      }

      reconnectInFlight = true
      const reconnectingSocket = socketRef.current
      const reconnectingAccessKey = activeAccessKeyRef.current
      const isCurrent = () =>
        reconnectingAccessKey === activeAccessKeyRef.current &&
        reconnectingSocket === socketRef.current
      try {
        if (socketRef.current) {
          if (force && socketRef.current.connected) {
            // Probe before tearing down - the connection usually survives
            // sleep/tab switches, and reconnecting drops realtime for nothing
            const isHealthy = await pingSocket(socketRef.current)
            if (!isCurrent()) return
            if (isHealthy) {
              logger.info(
                '[SocketProvider] Socket healthy after app wake, skipping reconnect',
                {
                  reason: options?.reason,
                  socketId: socketRef.current.id,
                },
              )
              if (isTokenNearExpiry()) {
                await refreshSocketAuthRef.current()
              }
              return
            }

            logger.info(
              '[SocketProvider] Socket unresponsive after app wake, reconnecting',
              {
                reason: options?.reason,
                socketId: socketRef.current?.id,
                userId: user.id,
                organizationId: scopedOrganizationId,
              },
            )
            consecutiveSuccessfulChecks.current = 0
            currentHealthCheckInterval.current = HEALTH_CHECK_INTERVAL_MIN
            await refreshSocketAuthRef.current(true)
            return
          }

          if (reconnectAttempts.current >= 12) {
            logger.warn(
              '[SocketProvider] Recreating socket after repeated reconnect failures',
              {
                attempts: reconnectAttempts.current,
                userId: user.id,
                organizationId: scopedOrganizationId,
              },
            )
            socketRef.current.removeAllListeners()
            socketRef.current.disconnect()
            socketRef.current = null
            setSocket(null)
            setIsConnected(false)
            setIsReady(false)
            setSocketId(null)

            const socketToken = await getSocketTokenWithRefreshRef.current()
            await createSocketConnectionRef.current(socketToken)
            reconnectAttempts.current = 0
            return
          }

          await refreshSocketAuthRef.current()
          if (isCurrent()) reconnectingSocket?.connect()
        } else {
          const socketToken = await getSocketTokenWithRefreshRef.current()
          await createSocketConnectionRef.current(socketToken)
        }
      } catch (error) {
        logger.error('[SocketProvider] Reconnect failed:', error)
      } finally {
        reconnectInFlight = false
      }
    }

    const reconnectAfterLifecycleEvent = (reason: string) => {
      const now = Date.now()
      const idleMs = now - lastLifecycleEventAtRef.current
      lastLifecycleEventAtRef.current = now

      const shouldForceReconnect =
        idleMs >= WAKE_RECONNECT_IDLE_MS &&
        now - lastWakeReconnectAtRef.current >= WAKE_RECONNECT_THROTTLE_MS

      if (shouldForceReconnect) {
        lastWakeReconnectAtRef.current = now
        void attemptReconnect({ force: true, reason })
        return
      }

      void attemptReconnect({ reason })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reconnectAfterLifecycleEvent('visibility-visible')
      }
    }

    const handleFocus = () => {
      reconnectAfterLifecycleEvent('window-focus')
    }

    const handleOnline = () => {
      reconnectAfterLifecycleEvent('browser-online')
    }

    const handleReconnectRequest = (event: Event) => {
      const reason =
        (event as CustomEvent<{ reason?: string }>).detail?.reason ?? 'manual'
      logger.info('[SocketProvider] Reconnect requested', {
        reason,
        socketConnected: socketRef.current?.connected,
      })
      void attemptReconnect({ force: true, reason })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('online', handleOnline)
    window.addEventListener(
      SOCKET_RECONNECT_REQUEST_EVENT,
      handleReconnectRequest,
    )

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener(
        SOCKET_RECONNECT_REQUEST_EVENT,
        handleReconnectRequest,
      )
    }
  }, [user?.id, scopedOrganizationId])

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        isReady,
        socketId,
        requestReconnect: requestSocketReconnect,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}

/**
 * Hook to access socket context
 */
export function useSocket() {
  return useContext(SocketContext)
}
